import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '@/utils/api';
import type { Topic, Subtopic } from '@/types';

interface TopicsState {
  topics: Topic[];
  subtopics: Record<string, Subtopic[]>;
  currentTopic: Topic | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: TopicsState = {
  topics: [],
  subtopics: {},
  currentTopic: null,
  isLoading: false,
  error: null,
};

export const fetchTopics = createAsyncThunk(
  'topics/fetchTopics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/topics');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch topics'
      );
    }
  }
);

export const fetchTopic = createAsyncThunk(
  'topics/fetchTopic',
  async (topicId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/topics/${topicId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch topic'
      );
    }
  }
);

export const createTopic = createAsyncThunk(
  'topics/createTopic',
  async (data: Partial<Topic>, { rejectWithValue }) => {
    try {
      const response = await api.post('/topics', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create topic'
      );
    }
  }
);

export const updateTopic = createAsyncThunk(
  'topics/updateTopic',
  async ({ id, data }: { id: string; data: Partial<Topic> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/topics/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update topic'
      );
    }
  }
);

export const deleteTopic = createAsyncThunk(
  'topics/deleteTopic',
  async (topicId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/topics/${topicId}`);
      return topicId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to delete topic'
      );
    }
  }
);

export const fetchSubtopics = createAsyncThunk(
  'topics/fetchSubtopics',
  async (topicId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/topics/${topicId}/subtopics`);
      return { topicId, subtopics: response.data };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch subtopics'
      );
    }
  }
);

export const createSubtopic = createAsyncThunk(
  'topics/createSubtopic',
  async ({ topicId, data }: { topicId: string; data: Partial<Subtopic> }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/topics/${topicId}/subtopics`, data);
      return { topicId, subtopic: response.data };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create subtopic'
      );
    }
  }
);

export const updateSubtopic = createAsyncThunk(
  'topics/updateSubtopic',
  async (
    { topicId, subtopicId, data }: { topicId: string; subtopicId: string; data: Partial<Subtopic> },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(`/topics/${topicId}/subtopics/${subtopicId}`, data);
      return { topicId, subtopic: response.data };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update subtopic'
      );
    }
  }
);

export const deleteSubtopic = createAsyncThunk(
  'topics/deleteSubtopic',
  async ({ topicId, subtopicId }: { topicId: string; subtopicId: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/topics/${topicId}/subtopics/${subtopicId}`);
      return { topicId, subtopicId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to delete subtopic'
      );
    }
  }
);

const topicsSlice = createSlice({
  name: 'topics',
  initialState,
  reducers: {
    setCurrentTopic: (state, action: PayloadAction<Topic | null>) => {
      state.currentTopic = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch topics
      .addCase(fetchTopics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTopics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.topics = action.payload;
      })
      .addCase(fetchTopics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch single topic
      .addCase(fetchTopic.fulfilled, (state, action) => {
        state.currentTopic = action.payload;
      })
      // Create topic
      .addCase(createTopic.fulfilled, (state, action) => {
        state.topics.push(action.payload);
      })
      // Update topic
      .addCase(updateTopic.fulfilled, (state, action) => {
        const index = state.topics.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.topics[index] = action.payload;
        }
        if (state.currentTopic?.id === action.payload.id) {
          state.currentTopic = action.payload;
        }
      })
      // Delete topic
      .addCase(deleteTopic.fulfilled, (state, action) => {
        state.topics = state.topics.filter((t) => t.id !== action.payload);
        if (state.currentTopic?.id === action.payload) {
          state.currentTopic = null;
        }
      })
      // Fetch subtopics
      .addCase(fetchSubtopics.fulfilled, (state, action) => {
        state.subtopics[action.payload.topicId] = action.payload.subtopics;
      })
      // Create subtopic
      .addCase(createSubtopic.fulfilled, (state, action) => {
        const { topicId, subtopic } = action.payload;
        if (!state.subtopics[topicId]) {
          state.subtopics[topicId] = [];
        }
        state.subtopics[topicId].push(subtopic);
      })
      // Update subtopic
      .addCase(updateSubtopic.fulfilled, (state, action) => {
        const { topicId, subtopic } = action.payload;
        const subtopics = state.subtopics[topicId];
        if (subtopics) {
          const index = subtopics.findIndex((s) => s.id === subtopic.id);
          if (index !== -1) {
            subtopics[index] = subtopic;
          }
        }
      })
      // Delete subtopic
      .addCase(deleteSubtopic.fulfilled, (state, action) => {
        const { topicId, subtopicId } = action.payload;
        const subtopics = state.subtopics[topicId];
        if (subtopics) {
          state.subtopics[topicId] = subtopics.filter((s) => s.id !== subtopicId);
        }
      });
  },
});

export const { setCurrentTopic } = topicsSlice.actions;
export default topicsSlice.reducer;
